import {
    Button,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
} from "@mui/material";
import ResponsiveDialog from "shared/ui/ResponsiveDialog";
import { useTranslation } from "shared/hooks/useTranslation";
import type { ReactNode } from "react";

interface ConfirmDialogProps {
    open: boolean;
    title: string;
    description?: ReactNode;
    onConfirm: () => void;
    onCancel: () => void;
    confirmLabel?: string;
    cancelLabel?: string;
    confirmColor?: "error" | "primary" | "secondary" | "warning";
    confirmIcon?: ReactNode;
    loading?: boolean;
    loadingLabel?: string;
    /** When true, confirm stays disabled (e.g. offline read-only). */
    confirmDisabled?: boolean;
    children?: ReactNode;
}

export default function ConfirmDialog({
    open,
    title,
    description,
    onConfirm,
    onCancel,
    confirmLabel,
    cancelLabel,
    confirmColor = "primary",
    confirmIcon,
    loading = false,
    loadingLabel,
    confirmDisabled = false,
    children,
}: ConfirmDialogProps) {
    const { t } = useTranslation();

    return (
        <ResponsiveDialog open={open} onClose={onCancel} disableScrollLock>
            <DialogTitle>{title}</DialogTitle>
            <DialogContent>
                {description && (
                    <DialogContentText>{description}</DialogContentText>
                )}
                {children}
            </DialogContent>
            <DialogActions>
                <Button onClick={onCancel} color="primary" disabled={loading}>
                    {cancelLabel ?? t("common.cancel")}
                </Button>
                <Button
                    onClick={onConfirm}
                    color={confirmColor}
                    variant="contained"
                    disabled={loading || confirmDisabled}
                    startIcon={loading ? undefined : confirmIcon}
                >
                    {loading
                        ? (loadingLabel ?? t("common.loading"))
                        : (confirmLabel ?? t("common.confirm"))}
                </Button>
            </DialogActions>
        </ResponsiveDialog>
    );
}
